import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Tab,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import React, { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const InformationReturnTax = () => {
  const [activeTab, setActiveTab] = useState('1');

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <AppLayout
      meta={
        <Meta
          title="Information Returns Resource Center | W-2, 1099 & More"
          description="Explore information return resources for W-2, 1099, 1098, 5498, ACA forms, instructions, due dates, penalties, corrections, and more."
          keywords=""
          canonical="resources/information-returns/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ justifyContent: 'center', mt: 8, mb: 8 }}
        component="section"
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 2,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Information Returns</span>
          </span>{' '}
          Resource Center
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 2,
            fontWeight: 600,
          }}
        >
          Everything You Need to Understand, Prepare, and File Tax Information
          Returns
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 3,
          }}
        >
          Complete guidance on W-2, 1099, 1098, 5498, ACA forms, and other
          information returns <br />
          with filing instructions, due dates, and compliance resources.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href="#explore"
          >
            Explore Information Returns Resources
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        mt={0}
        component="section"
        id="explore"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />

          <Box sx={{ width: '100%', height: 'auto', minHeight: '100%' }}>
            <TabContext value={activeTab}>
              <Box
                sx={{
                  borderRadius: 8,
                  p: '0px', // 👈 IMPORTANT: small padding creates visible border
                  backgroundImage:
                    'linear-gradient(#fff, #fff), linear-gradient(215deg, rgb(29,183,121) 0%, rgb(152,70,230) 100%)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  border: '2px solid transparent',
                  // maxWidth: 'md',
                  mx: 'auto',
                }}
              >
                <TabList
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  // value={activeTab}
                  onChange={handleChange}
                  centered
                  sx={{
                    color: '#000000',
                    textTransform: 'none',
                    rowGap: 1,
                    backgroundColor: '#ffffff',
                    px: 0,
                    borderRadius: 16,

                    '& .MuiTabs-indicator': {
                      backgroundColor: 'transparent !important',
                      height: 48,
                      borderRadius: 16,
                      transition:
                        'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    },
                    '& .Mui-selected': {
                      color: '#ffffff !important',
                      backgroundColor: '#333 !important',
                      transition: 'all 0.5s ease',
                    },
                    '& .MuiButtonBase-root': {
                      transition: 'all 0.5s ease',
                      px: 4,
                    },
                    '& .MuiTab-root': {
                      minHeight: 48,
                      textTransform: 'none',
                      backgroundColor: '#edebeb',
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      px: 3,
                      width: '100%',
                      maxWidth: 138.5,
                      color: '#333',
                      transition:
                        'transform 0.25s ease, background-color 0.3s ease, color 0.3s ease',
                    },
                    '& .MuiTab-root:hover': {
                      transform: 'translateY(0px)',
                    },
                    '& .MuiTab-root:active': {
                      transform: 'scale(0.95)',
                    },
                    '& .MuiTabs-flexContainer': {
                      m: 'auto',
                      display: { xs: 'flex', sm: 'flex' },
                      justifyContent: 'flex-start',
                      alignSelf: 'center',
                      // gridTemplateColumns: {
                      //   xs: 'repeat(2, 1fr)',
                      //   sm: 'repeat(4, 1fr)',
                      // },
                      gap: { xs: 1, sm: 1, md: 1 },
                    },
                  }}
                >
                  <Tab label="Form W-2" value="1" />
                  <Tab label="Form 1099" value="2" />
                  <Tab label="Form 1098" value="3" />
                  <Tab label="Form W-2G" value="4" />
                  <Tab label="Form 5498" value="5" />
                  <Tab label="ACA Forms" value="6" />
                  <Tab label="Others" value="7" />
                </TabList>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '1' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '1' ? 'auto' : 'none',
                  transform:
                    activeTab === '1' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel
                  value="1"
                  sx={{ height: 'auto', minHeight: '100%', px: 0 }}
                >
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Form W-2 Series
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for employers filing Wage and Tax Statements.
                        Learn how to prepare, distribute, and correct Form W-2,
                        stay current with annual updates, and meet filing and
                        mailing deadlines.
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Wage and Tax Statements
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Form W-2, employer reporting requirements,
                        employee copies, filing requirements, and related forms.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2 Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn about Form W-2 filing requirements, key
                              deadlines, and step-by-step filing instructions
                              for employers.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read W-2 Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w-2-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2 Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Understand Form W-2 due dates and filing deadlines
                              for both paper and electronic submissions.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w2-mailing-address/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            IRS Mailing Address
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2 Mailing Address
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Find the correct IRS mailing address for filing
                              paper Form W-2 based on your business location.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Mailing Address
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w-2c-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Corrections
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2c Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              How to correct errors on a previously filed Form
                              W-2 using Form W-2c and provide updated copies to
                              employees.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read W-2c Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Form W-2 Updates
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Stay current with the latest Form W-2 changes and
                        requirements for the 2025 tax year.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/2025-form-w2-updates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Updates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              2025 W-2 Updates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Review the latest changes to Form W-2 for the 2025
                              tax year, including new reporting requirements.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View 2025 Updates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/2024-form-w2-updates-filing-guide/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Updates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              2024 Form W-2 Updates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reference prior year updates and changes that
                              affected Form W-2 reporting for 2024.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View 2024 Updates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w2-penalties/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Compliance
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              W-2 Penalties for 2025
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn about penalties for late filing, incorrect
                              information, and other W-2 compliance violations.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Learn About Penalties
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Form W-2 Comparisons
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Compare Form W-2 with related tax forms to understand
                        their differences, filing purposes, reporting
                        <br />
                        requirements, and when each form is used.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w2-vs-w4/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Comparisons
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2 vs Form W-4
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Understand the difference between Form W-2 and
                              Form W-4 and how each form is used in the payroll
                              reporting process.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Compare W-2 vs W-4
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w2-vs-w9/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Comparisons
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2 vs W-9
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Compare Form W-2 and Form W-9, including who uses
                              each form and when it is required.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Compare W-2 vs W-9
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '2' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '2' ? 'auto' : 'none',
                  transform:
                    activeTab === '2' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="2" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Form 1099 Series
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for the most common 1099 information returns —
                        from contractor payments and investment income to debt
                        cancellations, retirement distributions, and digital
                        asset reporting.
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Common 1099 Forms
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Explore common Form 1099 types and learn which forms are
                        used to report different types of income, payments, and
                        transactions.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-nec-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-NEC Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report nonemployee compensation for contractors
                              and self-employed individuals
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-NEC Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-misc-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-MISC Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              File miscellaneous income including rents, prizes,
                              medical payments, and other compensation.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-MISC Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-k-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-K Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report payment card transactions and third-party
                              network transactions.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-K Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-g-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-G Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report government payments including unemployment
                              benefits and state tax refunds.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-G Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Investment, Interest & Cooperative Distributions
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Forms 1099 used to report investment income,
                        interest, dividends, securities transactions, original
                        issue discount, and certain cooperative distributions.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-int-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-INT Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report interest income from bonds, savings
                              accounts, and other interest-bearing investments.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-INT Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-div-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-DIV Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report dividend distributions from stocks and
                              mutual funds to shareholders.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-DIV Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-b-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-B Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting proceeds from broker and barter exchange
                              transactions, including cost basis.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-B Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-oid-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-OID Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report original issue discount on bonds and debt
                              instruments.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-OID Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-patr-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-PATR Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report patronage dividends and other distributions
                              from cooperatives.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-PATR Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Debt, Property & Real Estate
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Forms 1099 used to report canceled debt,
                        property acquisition or abandonment, and certain real
                        estate transactions.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-a-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-A Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn how Form 1099-A is used to report the
                              acquisition or abandonment of secured property in
                              connection with a debt.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-A Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-c-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-C Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              How to report cancellation of debt and the
                              exceptions that may exclude it from income.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-C Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-s-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-S Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting proceeds from real estate transactions,
                              including the responsible person rules.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-S Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Retirement, Education, Health & Long-Term Care
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Forms 1099 used to report retirement
                        distributions, education program payments, health
                        savings account distributions, and certain long-term
                        care benefits.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-r-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Retirement
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-R Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting distributions from pensions, annuities,
                              IRAs, and other retirement plans.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-R Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-q-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Education
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-Q Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report distributions from Coverdell ESA and
                              qualified tuition programs.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-Q Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-sa-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Health Savings
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-SA Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting distributions from HSAs, Archer MSAs,
                              and Medicare Advantage MSAs.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-SA Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-ltc-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Long-Term Care
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-LTC Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting payments under long-term care insurance
                              contracts and accelerated death benefits.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-LTC Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        1099 Series Due Dates
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Understand the filing and recipient deadlines for Forms
                        1099, including when to file electronically or on paper
                        and when recipient copies are due
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099 Series Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn about furnishing and IRS filing deadlines
                              for Forms 1099, including Forms 1099-NEC,
                              1099-MISC, and other information returns.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Digital Assets
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Form 1099-DA and the reporting requirements
                        for digital asset transactions, including when brokers
                        must report digital asset sales and related transactions
                        to the IRS.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1099-da-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Digital Assets
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1099-DA Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn how brokers report proceeds from digital
                              asset transactions on Form 1099-DA, including
                              reporting requirements and information provided to
                              taxpayers.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1099-DA Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '3' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '3' ? 'auto' : 'none',
                  transform:
                    activeTab === '3' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="3" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Form 1098 Series
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for filers of Form 1098 statements — covering
                        mortgage interest, student loan interest, tuition, and
                        charitable contributions of vehicles and other payments.
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Mortgage, Student Loan & Tuition Forms
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Explore Form 1098 resources for reporting mortgage
                        interest, student loan interest, tuition payments, and
                        other education-related information.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098 Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn how mortgage lenders and other eligible
                              recipients report mortgage interest received from
                              borrowers on Form 1098.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098 Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-e-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-E Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report qualified student loan interest paid by
                              borrowers for education expenses.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098-E Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-t-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-T Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting tuition and related expenses for
                              students enrolled at an eligible educational
                              institution.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098- T Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>

                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Other Form 1098 Statements
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn about Forms 1099 used to report investment income,
                        interest, dividends, securities transactions, original
                        issue discount, and certain cooperative distributions.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-c-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-C Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report charitable contributions of motor vehicles
                              and report vehicle donation values.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098-C Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-f-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-F Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn how applicable organizations report fines,
                              penalties, and other amounts paid in connection
                              with violations or potential violations of law.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098-F Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-q-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-Q Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Learn how issuers report information about
                              Qualified Longevity Annuity Contracts (QLACs) on
                              Form 1098-Q.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1098-Q Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>

                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Form 1098 Due Dates
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Find the filing and recipient deadlines for Form 1098
                        series statements, including when forms <br />
                        must be furnished and filed with the IRS.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-series-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              1098 Series Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Furnishing and filing deadlines that apply across
                              the 1098 series of statements.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-c-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-C Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              When to furnish Form 1098-C to the donor and file
                              it with the IRS.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-f-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-F Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Filing and furnishing deadlines for Form 1098-F
                              reportable payments.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1098-q-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1098-Q Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Deadlines for furnishing and filing Form 1098-Q
                              QLAC statements..
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '4' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '3' ? 'auto' : 'none',
                  transform:
                    activeTab === '4' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="4" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Form W-2G Resource Center
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Complete guidance on gambling winnings, reporting
                        requirements, due dates, tax withholding, and compliance
                        for payers and winners.
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Certain Gambling Winnings
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Learn when Form W-2G is used to report certain gambling
                        winnings, including applicable reporting and withholding
                        requirements.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w-2g-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2G Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Understand Form W-2G reporting requirements,
                              withholding rules, and filing procedures.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read W-2G Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-w-2g-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form W-2G Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Filing and furnishing deadlines for Form W-2G to
                              winners and the IRS.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '5' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '5' ? 'auto' : 'none',
                  transform:
                    activeTab === '5' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="5" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Form 5498 Series
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for trustees and custodians reporting
                        contributions, rollovers, and year-end balances for
                        IRAs, Coverdell education savings accounts, and health
                        savings accounts.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Retirement
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498 Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting IRA contributions, rollovers,
                              conversions, and required minimum distribution
                              information.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 5498 Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-esa-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498-ESA Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting contributions and rollovers to Coverdell
                              Education Savings Accounts.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 5498-ESA Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-sa-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498-SA Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting contributions to HSAs, Archer MSAs, and
                              Medicare Advantage MSAs.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 5498-SA Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-qa-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498-QA Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting contributions, rollovers, direct
                              program-to-program transfers, and fair market
                              value information for ABLE accounts.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 5498-SA Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>

                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        Form 5498 Series Due Dates
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Find the filing and recipient deadlines for Form 5498
                        series statements, including Forms 5498, 5498-ESA,
                        5498-SA, and 5498-QA.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498 Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Annual filing deadline for traditional and Roth
                              IRA contribution statements.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-esa-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498-ESA Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Filing and furnishing deadlines for Form 5498-ESA
                              Coverdell ESA reporting.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-5498-sa-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 5498-SA Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Deadlines for furnishing and filing Form 5498-SA,
                              HSA and MSA statements.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '6' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '6' ? 'auto' : 'none',
                  transform:
                    activeTab === '6' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="6" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        ACA Forms
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for ALEs and coverage providers reporting
                        under the Affordable Care Act — including Forms 1095-B
                        and 1095-C, due dates, penalties, and the codes used to
                        report offers of coverage.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1095-b-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1095-B Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              How coverage providers report minimum essential
                              coverage offered to individuals on Form 1095-B.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1095-B Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1095-c-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1095-C Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              How applicable large employers report offers of
                              coverage and employee enrollment on Form 1095-C.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1095-C Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1095-b-vs-1095-c/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1095-B vs Form 1095-C
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Which form your organization files, based on size
                              and coverage type.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Compare 1095-B vs 1095-C
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>

                    <Grid
                      size={{ xs: 12, md: 12, sm: 12 }}
                      sx={{ borderTop: ' 1px solid #ccc' }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.8,
                          mt: 3,
                          fontSize: { md: 20, sm: 17, xs: 16 },
                        }}
                      >
                        ACA Due Dates, Penalties &amp; Codes
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Understand ACA reporting due dates, potential penalties
                        for late or incorrect filings, and the codes used to
                        report health coverage information on Forms 1095-B and
                        1095-C.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/aca-form-due-date/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              ACA Forms Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Furnishing and filing deadlines for Form 1095-B
                              and 1095-C, including any extended deadlines.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/aca-forms-penalties/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Compliance
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              ACA Forms Penalties
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Penalty amounts for late or incorrect ACA
                              information returns.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Learn About Penalties
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1095-c-codes/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Most Helpful
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              ACA Forms Codes
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              How to read the offer and coverage codes used on
                              Form 1095-C.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View ACA Codes
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/aca-forms-bulk-upload/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Filing Tools
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 20, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              ACA Bulk Upload
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Upload ACA form data in bulk to simplify
                              preparation and e-filing of multiple Forms 1095-B
                              and 1095-C.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Explore Bulk Upload
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  opacity: activeTab === '7' ? 1 : 0,
                  // transition: 'opacity 0.4s ease',
                  pointerEvents: activeTab === '7' ? 'auto' : 'none',
                  transform:
                    activeTab === '7' ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <TabPanel value="7" sx={{ px: 0 }}>
                  <Grid
                    container
                    spacing={3}
                    rowSpacing={4}
                    m={0}
                    sx={{ justifyContent: 'center' }}
                  >
                    <Grid size={{ xs: 12, md: 12, sm: 12 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Other Information Returns
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                        }}
                      >
                        Form 1097-BTC, 3921, 3922, 1042-S{' '}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Resources for less-common information returns — bonus
                        tax credits, and stock transfers under employee purchase
                        and incentive plans.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1097-btc-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1097-BTC Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Reporting bonus tax credits transferred to
                              recipients from certain tax credit bonds
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1097-BTC Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-3921-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Stock options
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 3921 Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report exercises of incentive stock options (ISOs)
                              and provide documentation to employees.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 3921 Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-3922-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Stock options
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 3922 Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Report exercises of employee stock purchase plans
                              (ESPPs) and share transfers to employees.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 3922 Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1097-btc-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              FForm 1097-BTC Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Furnishing and filing deadlines for reporting
                              bonus tax credit transfers.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-3921-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 3921 Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Understand when Form 3922 must be furnished to
                              employees and filed with the IRS for employee
                              stock purchase plan transactions.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-3922-due-dates/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Important Dates
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 3922 Due Dates
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Deadlines for furnishing and filing Form 3922 ISO
                              transfer statements.
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              View Due Dates
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                      <Link
                        href="/resources/information-returns/form-1042-s-instructions/"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 2,
                            position: 'relative',
                            borderRadius: 3,
                            pt: 3,
                            pb: 2,
                            minHeight: '100%',
                            justifyContent: 'center',
                            display: 'grid',
                            alignContent: 'space-between',
                            transition:
                              'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                              transform: 'rotate(-225deg)',
                            },
                            '&:hover': {
                              boxShadow:
                                ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                              transform: 'translateY(-0.1rem)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              transition:
                                'transform 0.3s ease, box-shadow 0.3s ease',
                              zIndex: 2,
                              position: 'absolute',
                              fontSize: { md: 13, sm: 12, xs: 11 },
                              borderRadius: 24,
                              backgroundColor: '#e1e8ff',
                              color: '#5174e3',
                              fontWeight: 500,
                              border: '1px solid #5174e3',
                              px: 2,
                              display: 'inline-block',
                              top: -12,
                              left: 15,
                            }}
                          >
                            Essentials
                          </Typography>

                          <Box>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 600,
                                fontSize: { md: 22, sm: 19, xs: 16 },
                                mb: 0.5,
                              }}
                            >
                              Form 1042-S Instructions
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                              Guidance on reporting U.S.-source income paid to
                              foreign persons, including withholding, income
                              codes, recipient information, and filing
                              requirements
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              textAlign: 'left',
                              gap: 1.2,
                              color: '#0202ff',
                            }}
                          >
                            <Typography variant="body1">
                              Read 1042-S Instructions
                            </Typography>
                            <KeyboardBackspaceIcon
                              sx={{
                                transform: 'rotate(-180deg)',
                                transition: 'transform 0.3s ease',
                              }}
                            />
                          </Box>
                        </Paper>
                      </Link>
                    </Grid>
                  </Grid>
                </TabPanel>
              </Box>
            </TabContext>
          </Box>
        </Container>
      </Grid>

      <Container
        maxWidth="lg"
        sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}
        component="section"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need Help Filing Your Information Returns?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Have the information you need? E-file Form W-2, 1099, 1098, 5498,
              and ACA forms online <br />
              with a guided e-filing experience — get recipient copies and IRS
              acceptance quickly.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=informationreturns`}
              >
                E-file Information Returns
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default InformationReturnTax;
